import { Box, Button, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import type { PackItem } from '../RafflePage';
import {
  getFirstMonthDiscountedPrice,
  isDiscountEligibleSubscriptionPackage,
  isTopSubscriptionPackage
} from '../../../../../../../utils/subscriptionDiscounts';

type SubscriptionUpgradeCardsProps = {
  items: PackItem[];
  selectedId?: string;
  onSelect: (id: string) => void;
};

const money = (value: number | string | undefined) => {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) return String(value ?? '');
  return amount.toFixed(2);
};

const getBonusStartingEntries = (pack: PackItem) =>
  Math.max(0, Math.floor(Number(pack.entries || 0) * 10));

const getWeeklyEquivalent = (monthlyPrice: number) => (monthlyPrice * 12) / 52;

const SubscriptionUpgradeCards = ({
  items,
  selectedId,
  onSelect
}: SubscriptionUpgradeCardsProps) => {
  const entryCounts = items.map((item) => Number(item.entries || 0));
  const minEntries = Math.min(...entryCounts);
  const maxEntries = Math.max(...entryCounts);

  /* Never lay out more columns than there are cards.
   *
   * The track counts were fixed at 2 and 4, which is right for the four-pack
   * giveaway page but leaves dead columns for any caller showing a subset:
   * `/giveaway-offer/confirmation` passes two, and above 1536px they were pinned
   * to the left of an 1840px grid with two empty tracks beside them.
   *
   * The four-item case is unchanged by construction — min(4,2)=2, min(4,4)=4 and
   * 4 x 460 = 1840 — so the live giveaway page renders exactly as before. The
   * `maxWidth` scales with the count so a short list gets cards the size of a
   * full row's rather than two enormous ones, and `|| 1` keeps an empty list from
   * emitting `repeat(0, ...)`, which is invalid CSS. */
  const mdColumns = Math.min(items.length, 2) || 1;
  const xlColumns = Math.min(items.length, 4) || 1;

  return (
    <Box
      sx={{
        display: 'grid',
        width: '100%',
        maxWidth: { xl: xlColumns * 460 },
        mx: 'auto',
        gridTemplateColumns: {
          xs: '1fr',
          md: `repeat(${mdColumns}, minmax(0, 1fr))`,
          xl: `repeat(${xlColumns}, minmax(0, 1fr))`
        },
        gap: { xs: 2.2, md: 3, xl: 4 },
        alignItems: 'stretch'
      }}
    >
      {items.map((pack, index) => {
        const isBestValue = isTopSubscriptionPackage(pack.id);
        const hasDiscount = isDiscountEligibleSubscriptionPackage(pack.id);
        const isSelected = selectedId === pack.id;
        const excitement =
          maxEntries === minEntries
            ? 1
            : (Number(pack.entries || 0) - minEntries) /
              (maxEntries - minEntries);
        const isMuted = excitement < 0.2;
        const isQuiet = excitement >= 0.2 && excitement < 0.5;
        const isBalanced = excitement >= 0.5 && excitement < 0.8;
        const price = Number(pack.price || 0);
        const discountedPrice = getFirstMonthDiscountedPrice(price);
        const displayPrice = hasDiscount ? discountedPrice : price;
        const weeklyPrice = getWeeklyEquivalent(displayPrice);
        const regularWeeklyPrice = getWeeklyEquivalent(price);
        const nftCount = Number(pack.nfts || 0);
        const tokenCount = Number(pack.tokens || 0);
        const bonusEntries = getBonusStartingEntries(pack);
        const accentColor = isBestValue
          ? '#92400e'
          : isMuted
            ? '#475569'
            : isQuiet
              ? '#0f766e'
              : isBalanced
                ? '#0369a1'
                : '#0369a1';
        const badgeBackground = isBestValue
          ? '#f2b400'
          : isMuted
            ? '#f1f5f9'
            : isQuiet
              ? 'rgba(15,118,110,0.08)'
              : isBalanced
                ? 'rgba(8,145,178,0.10)'
                : 'rgba(8,145,178,0.10)';
        const ctaBackground =
          'linear-gradient(90deg, #0f8f87 0%, #0f766e 100%)';
        const ctaHoverBackground =
          'linear-gradient(90deg, #0e8079 0%, #115e59 100%)';

        return (
          <Box
            key={pack.id}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '8px',
              border: isBestValue
                ? '3px solid #f2b400'
                : isSelected
                  ? '3px solid #0369a1'
                  : isMuted
                    ? '3px solid rgba(15,23,42,0.32)'
                    : isQuiet
                      ? '3px solid rgba(15,118,110,0.38)'
                      : '3px solid rgba(8,145,178,0.36)',
              background: isBestValue
                ? 'linear-gradient(135deg, #fffdf2 0%, #fff7bf 100%)'
                : isMuted
                  ? 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)'
                  : isQuiet
                    ? 'linear-gradient(180deg, #ffffff 0%, #fbfffe 100%)'
                    : '#ffffff',
              p: { xs: 2.8, md: 3.4, xl: 3.8 },
              boxShadow: isBestValue
                ? '0 22px 60px rgba(242,180,0,0.22)'
                : isSelected
                  ? '0 18px 44px rgba(8,145,178,0.18)'
                  : isMuted
                    ? '0 14px 34px rgba(15,23,42,0.09)'
                    : isQuiet
                      ? '0 16px 38px rgba(15,118,110,0.12)'
                      : '0 18px 44px rgba(8,145,178,0.12)',
              display: 'flex',
              flexDirection: 'column',
              minHeight: { xs: 0, xl: 0 },
              height: '100%'
            }}
          >
            {isBestValue && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  px: { xs: 1.9, sm: 2.3 },
                  py: { xs: 0.85, sm: 1.05 },
                  borderBottomLeftRadius: '8px',
                  background:
                    'linear-gradient(90deg, #f2b400 0%, #ff9f0a 100%)',
                  color: '#3f2a00',
                  fontSize: { xs: 12, sm: 14 },
                  fontWeight: 950,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase'
                }}
              >
                Best value
              </Box>
            )}

            <Typography
              sx={{
                alignSelf: 'flex-start',
                mt: 0,
                borderRadius: 999,
                background: badgeBackground,
                color: isBestValue ? '#3f2a00' : accentColor,
                px: isBestValue ? { xs: 1.25, sm: 1.45 } : 1.8,
                py: 0.55,
                fontSize: 14,
                fontWeight: 900,
                maxWidth: isBestValue
                  ? { xs: '56%', sm: '58%', md: '54%' }
                  : '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {isBestValue
                ? 'Maximum entry'
                : isMuted
                  ? index === 0
                    ? 'Starter plan'
                    : 'Basic plan'
                  : isQuiet
                    ? 'Member upgrade'
                    : 'Popular upgrade'}
            </Typography>

            <Typography
              component="h2"
              sx={{
                mt: 2.2,
                minHeight: { xl: 0 },
                color: '#111827',
                // xs is a real step down: at 375px a 34px title fills the card
                fontSize: { xs: 26, sm: 30, md: 36 },
                fontWeight: 950,
                lineHeight: 1.03
              }}
            >
              {pack.title}
            </Typography>

            <Box
              sx={{
                mt: 0.9,
                minHeight: { xl: 0 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 0.65,
                  flexWrap: 'nowrap',
                  maxWidth: '100%',
                  whiteSpace: 'nowrap',
                  justifyContent: 'center'
                }}
              >
                <Typography
                  sx={{
                    color: '#111827',
                    fontSize: { xs: 30, sm: 34, md: 40 },
                    fontWeight: 950,
                    lineHeight: 0.95
                  }}
                >
                  A${weeklyPrice.toFixed(2)}
                </Typography>
                <Typography
                  sx={{
                    color: '#475569',
                    fontSize: { xs: 14, md: 15 },
                    fontWeight: 850,
                    lineHeight: 1
                  }}
                >
                  /week*
                </Typography>
                {hasDiscount && (
                  <Typography
                    sx={{
                      color: '#64748b',
                      fontSize: { xs: 14, md: 15 },
                      fontWeight: 800,
                      lineHeight: 1,
                      textDecoration: 'line-through',
                      textDecorationThickness: '2px'
                    }}
                  >
                    A${money(regularWeeklyPrice)}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Bonus banner above the per-draw entries line, by request.
             *
             * This swap reaches BOTH places these cards appear — `/giveaway-subs`
             * (RafflePage renders this same component for subscription mode) and
             * `/giveaway-offer/confirmation`. There is no separate change for the
             * subs page.
             *
             * `SubscriptionCarousel` carries its own copy of these two elements
             * and has NOT been swapped, so the two components now order them
             * differently. */}
            <Box
              sx={{
                mt: 2,
                borderRadius: '8px',
                border: '1px solid rgba(5,150,105,0.30)',
                background:
                  'linear-gradient(135deg, rgba(209,250,229,0.96) 0%, rgba(236,253,245,0.96) 100%)',
                px: 2,
                py: 1.5,
                boxShadow: '0 12px 26px rgba(5,150,105,0.10)'
              }}
            >
              <Typography
                sx={{
                  color: '#047857',
                  fontSize: { xs: 15, md: 16 },
                  fontWeight: 950,
                  lineHeight: 1.3,
                  textTransform: 'uppercase'
                }}
              >
                Bonus offer: start with {bonusEntries.toLocaleString('en-AU')}{' '}
                entries from the get go!
              </Typography>
            </Box>

            <Box
              sx={{
                mt: 1.8,
                py: 1.45,
                borderTop: '1px solid rgba(15,23,42,0.14)',
                borderBottom: '1px solid rgba(15,23,42,0.14)'
              }}
            >
              <Typography
                sx={{
                  minHeight: { xl: 0 },
                  color: accentColor,
                  fontSize: { xs: 17, sm: 19, md: 22 },
                  fontWeight: 900,
                  textAlign: 'center',
                  lineHeight: 1.3
                }}
              >
                Get {pack.entries.toLocaleString('en-AU')} stacking giveaway
                entries into every draw
              </Typography>
            </Box>

            <Box sx={{ mt: 2.1, display: 'grid', gap: 1.15 }}>
              {[
                `${nftCount.toLocaleString('en-AU')} Digital Collectible rewards per month`,
                `A$${tokenCount.toLocaleString(
                  'en-AU'
                )} worth of INC tokens per month`,
                'Cancel anytime'
              ].map((benefit) => {
                const isCancelBenefit = benefit === 'Cancel anytime';

                return (
                  <Box
                    key={benefit}
                    sx={{
                      display: 'flex',
                      gap: 1.4,
                      alignItems: 'flex-start',
                      /* Gold on EVERY plan, by request (2026-10-05): it used to
                         take the gold treatment only on the "best value" card and
                         a teal tint elsewhere, which read as two different
                         promises. Same literals as the gold card's frame. */
                      ...(isCancelBenefit
                        ? {
                            width: 'fit-content',
                            justifySelf: 'center',
                            justifyContent: 'center',
                            borderRadius: 999,
                            border: '1px solid rgba(202,138,4,0.28)',
                            background: 'rgba(242,180,0,0.13)',
                            px: 1.4,
                            py: 0.85
                          }
                        : {})
                    }}
                  >
                    {!isCancelBenefit && (
                      <CheckIcon
                        sx={{
                          mt: 0.15,
                          fontSize: 22,
                          color: isBestValue
                            ? '#d08a00'
                            : isMuted
                              ? '#64748b'
                              : isQuiet
                                ? '#0f766e'
                                : '#22c55e'
                        }}
                      />
                    )}
                    <Typography
                      sx={{
                        // the pill's text is the gold card's ink on every plan
                        color: isCancelBenefit ? '#92400e' : '#1f2937',
                        textAlign: isCancelBenefit ? 'center' : 'left',
                        fontSize: isCancelBenefit
                          ? { xs: 17, md: 18 }
                          : { xs: 16, md: 17 },
                        fontWeight: isCancelBenefit ? 950 : 700,
                        lineHeight: 1.35
                      }}
                    >
                      {benefit}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              type="button"
              onClick={() => onSelect(pack.id)}
              endIcon={<ArrowForwardIcon />}
              sx={{
                mt: 2,
                height: 58,
                borderRadius: '8px',
                color: '#ffffff',
                background: ctaBackground,
                fontSize: 15,
                fontWeight: 950,
                textTransform: 'uppercase',
                boxShadow: isBestValue
                  ? '0 16px 28px rgba(242,180,0,0.25)'
                  : isMuted
                    ? '0 12px 22px rgba(15,23,42,0.12)'
                    : isQuiet
                      ? '0 14px 24px rgba(15,118,110,0.12)'
                      : '0 16px 28px rgba(8,145,178,0.18)',
                '&:hover': {
                  background: ctaHoverBackground
                }
              }}
            >
              Start Subscription
            </Button>

            <Typography
              sx={{
                mt: 0.8,
                px: 0.5,
                color: '#64748b',
                fontSize: { xs: 10, md: 11 },
                fontWeight: 700,
                lineHeight: 1.3,
                textAlign: 'center'
              }}
            >
              {hasDiscount
                ? `*First-month equivalent. A$${money(
                    displayPrice
                  )} charged today, then A$${money(price)} monthly.`
                : `*Equivalent weekly price. Charged A$${money(
                    price
                  )} monthly.`}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default SubscriptionUpgradeCards;
